import { useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { DndContext, DragOverlay, closestCenter, type DragStartEvent, type DragEndEvent } from "@dnd-kit/core";
import { useListTree } from "../../lib/hooks/use-kuvio";
import { useAuth } from "../../lib/hooks/use-auth";
import { EmptyState } from "../components/primitives";
import type { LibraryNode } from "../../lib/types";
import { WorkspaceHeader } from "../components/scope/WorkspaceHeader";
import { ModuleSection } from "../components/scope/ModuleSection";
import { agentPersona } from "../lib/agent-persona";
import { ReparentPreview } from "../components/scope/ReparentPreview";
import { useWorkspaceInfo } from "../../lib/hooks/use-kuvio";
import { Avatar } from "../components/primitives";

function buildAncestorPath(nodeId: string, allNodes: LibraryNode[]): LibraryNode[] {
  const byId = new Map(allNodes.map((n) => [n.id, n]));
  const path: LibraryNode[] = [];
  let current = byId.get(nodeId);
  while (current?.parent_id) {
    const parent = byId.get(current.parent_id);
    if (!parent) break;
    path.unshift(parent);
    current = parent;
  }
  return path;
}

export function ScopePage() {
  const { currentWorkspaceId } = useAuth();
  const tree = useListTree(currentWorkspaceId ?? undefined);
  const ws = useWorkspaceInfo();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("selected");

  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [reparentState, setReparentState] = useState<{
    agent: LibraryNode;
    newParentId: string;
  } | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const allNodes = tree.data ?? [];

  const { modules, agentsByModule, agents, entityCount } = useMemo(() => {
    const mods = allNodes.filter((n) => n.type === "agent" && n.depth <= 1);
    const ags = allNodes.filter((n) => n.type === "agent" && n.depth > 1);
    const byMod = new Map<string, LibraryNode[]>();
    for (const mod of mods) byMod.set(mod.id, []);
    for (const ag of ags) {
      const parentId = ag.parent_id ?? "";
      if (!byMod.has(parentId)) byMod.set(parentId, []);
      byMod.get(parentId)!.push(ag);
    }
    const tables = allNodes.filter((n) => n.type === "table");
    return { modules: mods, agentsByModule: byMod, agents: ags, entityCount: tables.length };
  }, [allNodes]);

  const highlightedIds = useMemo(() => {
    if (!hoveredId) return new Set<string>();
    const ids = new Set<string>();
    const path = buildAncestorPath(hoveredId, allNodes);
    for (const n of path) ids.add(n.id);
    return ids;
  }, [hoveredId, allNodes]);

  const handleAgentClick = useCallback((agentId: string) => {
    setSearchParams({ selected: agentId });
  }, [setSearchParams]);

  function handleDragStart(event: DragStartEvent) {
    setDraggedId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDraggedId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const agentNode = allNodes.find((n) => n.id === active.id);
    const newParentId = over.id as string;
    if (!agentNode || agentNode.parent_id === newParentId) return;

    setReparentState({ agent: agentNode, newParentId });
  }

  // Sprint 23.5 — "new agent" navigates to the conversational Hire wizard,
  // optionally pre-filling reports_to via the parent_id query param.
  function openHire(parentId?: string) {
    navigate(parentId ? `/hire?parent_id=${encodeURIComponent(parentId)}` : "/hire");
  }

  const wsName = ws.data?.workspace?.name ?? "Workspace";

  if (tree.isLoading) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <div style={{ height: 36, width: 200, background: "var(--color-background-secondary)", borderRadius: 6, marginBottom: 28 }} />
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ height: 48, background: "var(--color-background-secondary)", borderRadius: 6, marginBottom: 8 }} />
        ))}
      </div>
    );
  }

  if (modules.length === 0) {
    return (
      <div style={{ fontFamily: "var(--font-sans)" }}>
        <WorkspaceHeader
          name={wsName}
          moduleCount={0}
          agentCount={0}
          entityCount={0}
          onNewAgent={() => openHire()}
        />
        <EmptyState>
          This workspace has no modules yet. Create your first agent to get started.
        </EmptyState>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button
            data-variant="primary"
            onClick={() => openHire()}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            Create first agent
          </button>
        </div>
      </div>
    );
  }

  const draggedNode = draggedId ? allNodes.find((n) => n.id === draggedId) : null;

  return (
    <div
      style={{ fontFamily: "var(--font-sans)", maxWidth: 680 }}
      onMouseLeave={() => setHoveredId(null)}
    >
      <WorkspaceHeader
        name={wsName}
        moduleCount={modules.length}
        agentCount={agents.length}
        entityCount={entityCount}
        onNewAgent={() => openHire()}
      />

      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {modules.map((mod) => (
          <div
            key={mod.id}
            onMouseEnter={() => setHoveredId(mod.id)}
          >
            <ModuleSection
              module={mod}
              agents={agentsByModule.get(mod.id) ?? []}
              selectedId={selectedId}
              highlightedIds={highlightedIds}
              onAgentClick={handleAgentClick}
              onAddAgent={(parentId) => openHire(parentId)}
            />
          </div>
        ))}

        <DragOverlay>
          {draggedNode ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 12px",
                background: "var(--color-background-primary)",
                border: "var(--kuvio-border-default)",
                borderRadius: "var(--border-radius-md)",
                fontSize: 13,
                fontWeight: 500,
                fontFamily: "var(--font-sans)",
              }}
            >
              <Avatar persona={agentPersona(draggedNode)} size="sm" name={draggedNode.name} />
              {draggedNode.name}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <div
        style={{
          marginTop: 20,
          padding: "10px 14px",
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-md)",
          fontSize: 12,
          color: "var(--color-text-tertiary)",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <i className="ti ti-info-circle" style={{ fontSize: 14 }} aria-hidden="true" />
        Drag agents between modules to reparent. The inherited context will update automatically.
      </div>

      {reparentState && (
        <ReparentPreview
          open
          agent={reparentState.agent}
          oldParentPath={buildAncestorPath(reparentState.agent.id, allNodes)}
          newParentPath={buildAncestorPath(reparentState.newParentId, allNodes).concat(
            allNodes.find((n) => n.id === reparentState.newParentId)!
          ).filter(Boolean)}
          newParentId={reparentState.newParentId}
          onClose={() => setReparentState(null)}
          onConfirmed={() => setReparentState(null)}
        />
      )}
    </div>
  );
}
